/**
 * Full content from SEPM_2026_01_X-Translator_Admin_Manual.pdf
 * Word-by-word extraction. Use &lt; for < and &gt; for > in HTML.
 */

// Helper to create content object
const C = (title, html) => ({ title, html });

export const content = {
  "1-introduction": C(
    "1 Introduction",
    `<p>This document describes the installation and configuration of the product SEPM X-Translator.</p>`
  ),

  "2-1-1-overview": C(
    "2.1.1 Overview",
    `<p>To install the SEPM X-Translator, follow these steps</p>
<ul>
<li>Install the license.</li>
<li>Load Magik software modules (among them x_translator_swaf/swift and x_translator_engine) into the Smallworld image. The easiest way to do this is to create a customer module to hold Magik configurations (e.g. x_translator_customer_cambridge). The customer module should then depend on x_translator_swaf and x_raster_swaf (Smallworld 4) or x_translator_swift and x_raster_swift (Smallworld 5).</li>
<li>Configure XML files for the SWAF application module</li>
<li>(Optional) Add and configure the ds_translator Smallworld database to the session under the name translator. Configure ACE and Style of the X-Translator database.</li>
</ul>`
  ),

  "2-1-2-upgrade": C(
    "2.1.2 Upgrade to a new SEPM Version",
    `<p>To upgrade the SEPM X-Translators proceed as follows:</p>
<ul>
<li>Replace the SEPM products (In Smallworld 4 x_translator, in Smallworld 5 additional application specific products are required, for example x_translator_pni).</li>
<li>Use the customer module without any modifications</li>
<li>Copy the licenses (in case you haven't changed the path to the directory holding the SEPM licenses as described below).</li>
<li>Follow additional installation instructions in the release notes.</li>
<li>Upgrade to Smallworld 5: Replace the dependencies in the customer module from x_translator_swaf to x_translator_swift and x_raster_swaf to x_raster_swift.</li>
</ul>`
  ),

  "2-1-3-license": C(
    "2.1.3 License, Path to directory holding SEPM licenses",
    `<p>The X-Translator uses the following license types</p>
<ul>
<li>Evaluation license</li>
<li>Enterprise license (server license): For each format, one license string is required</li>
</ul>
<p>SEPM or an authorised Reseller delivers the license strings. The licenses are delivered as one or more files with the file extension "*.lic".</p>
<p>To create an evaluation license the Volume Serial Number of the systems C-drive is needed. This number is displayed in the X-Translator's user interface, as soon as you try to transfer some data. The string of the format "XXXX-XXXX" can also be determined with the "dir" command.</p>
<p>The path to the directory holding the licenses can be determined as follows:</p>
<ol>
<li>Copy the licenses to the default directory x_translator\\licenses</li>
<li>Copy the licenses to an arbitrary place and set the environment variable SEPM_LICENSES_DIR (new in 2021-01).</li>
<li>Adapt the setting x_base_settings.licenses_dir in your customer configuration:</li>
</ol>
<pre>_pragma(classify_level=restricted,topic={x_base},usage=redefinable)
_method x_base_settings.licenses_dir
## Parameters :
## Returns : Directory holding SEPM licenses
## Function :
_if (a_dir &lt;&lt; system.getenv( "SEPM_LICENSES_DIR" )) _isnt _unset
_then
_return a_dir
_else
_local l_dir &lt;&lt; smallworld_product.products[:x_translator].directory
l_dir &lt;&lt; system.pathname_down( l_dir, "licenses" )
_return l_dir
_endif
_endmethod
$</pre>`
  ),

  "2-1-4-module-sw4": C(
    "2.1.4 Module x_translator_customer (Smallworld 4)",
    `<p>SEPM recommends making all customer-specific changes inside a customer specific module. The module x_translator_customer can be copied for that purpose (e.g. to x_translator_customer_abc).</p>
<p>This customer module should then depend on the modules x_translator_swaf and possibly x_raster_swaf (through module.def requires).</p>
<p>Configurations such as changes to default values can then be done inside the customer specific module. The customer module x_translator_customer_abc should be kept (along with customer configurations) outside of the x_translator product for minimal impact when upgrading to a new SEPM release.</p>
<p>To load the X-Translator with a build script the following commands are required:</p>
<pre>smallworld_product.add_product( &lt;&lt; Directory of the X-Translator product &gt;&gt; )
smallworld_product.add_product( &lt;&lt; Path to customer configurations &gt;&gt; )
sw_module_manager.load_module(:x_translator_customer_abc)</pre>
<p>In a production environment you will probably have a "top level module" which will require the necessary modules of the application. In this situation you will need to reference the customer module x_translator_customer_abc in the requires section of the module.def of the top level module.</p>`
  ),

  "2-1-5-module-sw5": C(
    "2.1.5 Module x_translator_customer (Smallworld 5)",
    `<p>If you follow the methodology described above for Smallworld 4, you will have a customer module x_translator_customer_abc to load the SEPM X-Translator. All you will need to do when upgrading to Smallworld 5 is to change the dependencies in the module.def as follows:</p>
<ul>
<li>x_translator_swaf &gt; x_translator_swift</li>
<li>x_raster_swaf &gt; x_raster_swift</li>
</ul>`
  ),

  "2-1-6-jar-magik": C(
    "2.1.6 Understanding Jar versus Magik Precedence in SEPM",
    `<p>In Smallworld 5, code is loaded from the Jar files if available and not from the Magik source files. For example, code is loaded from x_translator\\libs\\ x_translator.x_translator_customer_sw5.2024020.jar and not from x_translator_customer_sw5\\source\\x_translator_client_settings.magik, even if the Magik version is more recent as the corresponding Jar file.</p>
<p>Therefore, do not modify SEPM-provided Magik sources. Instead, create a new customer-specific module outside the SEPM product structure. For example x_translator_customer_abc and place custom settings in this new module to avoid conflicts during updates and to maintain clean separation between SEPM's product and customer-specific changes.</p>`
  ),

  "2-1-7-product-ser": C(
    "2.1.7 Loading Time Optimization (file product.ser)",
    `<p>To reduce product startup time, module definitions can be stored in a serialized file (product.ser) within the product directory. Since this file relies on Java serialization, it is often incompatible between different Smallworld Service Packs or Java versions (Error: InvalidClassException).</p>
<p>It is therefore recommended to generate the file directly within the target environment:</p>
<ul>
<li>Start your Smallworld session and add the product (e.g., via sw_module_manager.add_product()).</li>
<li>Execute the following command at the Magik prompt:</li>
</ul>
<pre>smallworld_product.products[:x_translator].save_serialised_module_definitions()
# optional additional SEPM products:
smallworld_product.products[:x_translator_nis].save_serialised_module_definitions()
smallworld_product.products[:x_translator_waste_water].save_serialised_module_definitions()</pre>
<ul>
<li>The file product.ser will be created in the product's main directory. On the next startup, Smallworld will automatically detect this file and load the module structures significantly faster.</li>
</ul>`
  ),

  "2-1-8-swaf-xml": C(
    "2.1.8 Configure SWAF application XML files",
    `<p>The following XML files in the application module need to be configured:</p>
<ul>
<li>Define the plugin x_translator_plugin in the config.xml file:</li>
</ul>
<pre>&lt;plugin name="x_translator_plugin" class_name="x_translator_plugin"/&gt;</pre>
<ul>
<li>Add the action x_activate_translator in the gui.xml file:</li>
</ul>
<pre>&lt;action name="x_translator_plugin.x_activate_translator" /&gt;</pre>
<p>For PNI please use the following entries in config.xml:</p>
<pre>&lt;plugin name="x_translator_plugin" class_name="x_translator_plugin"&gt;
&lt;properties&gt;
&lt;property name="map_manager_name" value="map_plugin"/&gt;
&lt;/properties&gt;
&lt;/plugin&gt;
&lt;plugin name="x_raster_plugin" class_name="x_raster_plugin"&gt;
&lt;properties&gt;
&lt;property name="map_manager_name" value="map_plugin"/&gt;
&lt;/properties&gt;
&lt;/plugin&gt;</pre>`
  ),

  "2-1-9-database": C(
    "2.1.9 X-Translator Database",
    `<p>The X-Translator Database can be generated with the translator_implementer module (see chapter "Install/Upgrade of the X-Translator Database" below). Alternatively, SEPM delivers empty databases for specific Smallworld versions in the x_translator\\data\\databases\\translator directory. SEPM support is happy to create an empty database for your Smallworld version, if those supplied with the product are not appropriate.</p>
<p>The database should be added to the session with manage_soc() under the name translator.</p>
<figure class="doc-image">
  <img src="/images/x-translator-sw/Connection-Specification-translator.png" alt="Connect Specification translator - configuring the translator database connection" class="max-w-full rounded-lg border border-gray-200 dark:border-gray-600" />
  <figcaption>Connect Specification translator dialog for configuring the translator database</figcaption>
</figure>
<p>After opening the database you should configure the coordinate system to match your main coordinate system used. It's a good practice to delete all coordinate systems not required, the script in x_translator\\scripts\\database\\delete_coordinate_systems.magik can be used for that purpose.</p>
<p>You'll need the following panoply of commands, depending on your requirements:</p>
<pre>v &lt;&lt; gis_program_manager.databases[:translator]
v.world.coordinate_system
v.switch(:write)
v.create_coordinate_systems()
v.world.set_coordinate_system_by_name(:uk_british_national_grid_mm)
system.delete_all_csys_except_gis_csys(:translator)
v.commit()</pre>`
  ),

  "2-1-10-database-install": C(
    "2.1.10 Install/Upgrade of the X-Translator Database",
    `<p>The following data models are available:</p>
<table><thead><tr><th>Version</th><th>Description</th></tr></thead>
<tbody><tr><td>2017-02</td><td>Install/Upgrade to data model version 2017-02</td></tr></tbody></table>
<p>Note: This section assumes you are on Smallworld 4.3 or higher. If you are using a lower Smallworld version, please contact SEPM support.</p>
<p>To install a new X-Translator Database, the following steps are required:</p>
<ul>
<li>Load the module translator_implementer: sw_module_manager.load_module(:translator_implementer)</li>
<li>Using manage_soc(), make sure to remove any references to translator and translator_case datasets (e.g. from previous tests)</li>
<li>ACE installation is done on the "Default" ACE. If you want to use another ACE, you can enter the following command right before running the implementer: translator_implementer.settings[:ace_name]&lt;&lt; "Electricity"</li>
<li>Run the Install/2017020 version of the implementer:</li>
<li>Check the coordinate system of the database and change it if required</li>
</ul>
<p>To upgrade an existing X-Translator Database, the following steps are required:</p>
<ul>
<li>Load the module translator_implementer: sw_module_manager.load_module(:translator_implementer)</li>
<li>Make sure that the X-Translator database is configured with names translator and translator_case:</li>
</ul>
<figure class="doc-image">
  <img src="/images/x-translator-sw/soc-configuration.png" alt="SOC Configuration showing translator and translator_case datasets" class="max-w-full rounded-lg border border-gray-200 dark:border-gray-600" />
  <figcaption>SOC Configuration with translator and translator_case</figcaption>
</figure>
<ul>
<li>Run the Upgrade/2017020 of the implementer:</li>
</ul>`
  ),

  "2-1-11-database-ace": C(
    "2.1.11 X-Translator Database - ACE",
    `<p>The X-Translator Database contains the following datamodels, please set them visible manually as required:</p>
<ul>
<li>Translator-Objects: Essential for DWG/DXF Import</li>
<li>Survey-Objects: Generic area-, line-, point- and text-objects (optional)</li>
<li>Area-Objects (optional)</li>
<li>KML-Objects for KML-Import (optional)</li>
</ul>
<figure class="doc-image">
  <img src="/images/x-translator-sw/object-configuration.png" alt="Object Configuration - ACE with TRANSLATOR objects (X-Translator History, Event, Coverage, etc.)" class="max-w-full rounded-lg border border-gray-200 dark:border-gray-600" />
  <figcaption>ACE Configuration Example for the SEPM X-Translator Database</figcaption>
</figure>`
  ),

  "2-1-12-database-style": C(
    "2.1.12 X-Translator Database - Style",
    `<p>Style dumps are provided in the directory: x_translator\\modules\\implementers\\translator_implementer\\resources\\Base\\datamodel</p>
<table><thead><tr><th>Style dump</th><th>Description</th></tr></thead>
<tbody>
<tr><td>auto_2019_02.style</td><td>Systems with Magnification factors configured.</td></tr>
<tr><td>pni_2017_02.style</td><td>System with Magnification factors configured always to 1.0.</td></tr>
<tr><td>survey.style</td><td>Styles for Survey objects.</td></tr>
<tr><td>y_x_point.style</td><td>Styles for the "y_x_point" Symbol (for displaying DXF POINT entities).</td></tr>
</tbody></table>
<p>Which styles to use will depend on the configuration of your scale magnification factors: If you have set them to match the scale of the map you can use the auto_2019_02.style file. If those factors are all set to 1.0 you'll need to use the pni_2017_02.style file.</p>
<figure class="doc-image">
  <img src="/images/x-translator-sw/scale-configuration.png" alt="Scale Configuration - magnification factors for Detail, Block, Neighbourhood, City, Region, Country" class="max-w-full rounded-lg border border-gray-200 dark:border-gray-600" />
  <figcaption>Scale Configuration with magnification factors</figcaption>
</figure>
<p>System with Magnification factors set, use auto_2019_02.style.</p>`
  ),

  "2-1-13-wgs84": C(
    "2.1.13 WGS84 Coordinate System",
    `<p>The SEPM X-Translator KML format needs the world_longlat_wgs84_degree coordinate system to be installed. In newer Smallworld databases the coordinate system should already be available in the ACE:</p>
<pre>Magik&gt;
gis_program_manager.ace_view.collections[:sw_gis!coordinate_system].at(:world_longlat_wgs84_degree)
$
ds_coordinate_system:(world_longlat_wgs84_degree)
Magik&gt;</pre>
<p>If you are dealing with an old Smallworld database, the world_longlat_wgs84_degree coordinate system may need to be installed with the following steps:</p>
<pre>v &lt;&lt; gis_program_manager.cached_dataset(:gis)
sw_module_manager.load_module(:map_projections_catalogue)
ds_coordinate_system.catalogue_defs[:world_longlat_wgs84_degree]
v.create_coordinate_systems(:world)</pre>`
  ),
};
